import React from 'react';
import {
  ClientErrorCodes,
  GAME_STATE_UPDATED,
  GameStateEnums,
  type GameStates,
  PRESENTER_MARKED_ANSWER_EVENT,
} from '@mentimeter/quiz-types';
import { FollowerRoutes } from '@mentimeter/quiz-types';
import { useQuizResults } from '../quiz-result-provider';
import { useQuizConfig } from '../quiz-config.presenter';
import { useQuizStatus } from './use-quiz-status';
import { useEmit } from './use-emit';
import { useSocketIoConnection } from './connection-provider';
import { useSubscribe } from './use-subscribe';
import { useSubscribePlayers } from './use-subscribe-players';
import { useSubscribeRespondents } from './use-subscribe-respondents';

export const useFollowerQuizGameState = () => {
  const config = useQuizConfig();
  const { emit } = useEmit(config);
  const { mutate } = useQuizResults();

  const { connection } = useSocketIoConnection();
  const { connectedWithDelay, timeOffset } = useQuizStatus(connection);

  const { connectedPlayers } = useSubscribePlayers();
  const { respondents } = useSubscribeRespondents();

  const [gameState, setGameState] = React.useState<GameStates>();

  useSubscribe({
    event: PRESENTER_MARKED_ANSWER_EVENT,
    callback: React.useCallback(() => void mutate(), [mutate]),
  });

  useSubscribe({
    event: GAME_STATE_UPDATED,
    callback: React.useCallback(
      (state) => {
        if (state.gameStateData.questionPublicKey !== config.questionPublicKey)
          return;
        if (state.gameState === GameStateEnums.RESULT) {
          mutate();
        }

        setGameState(state as GameStates);
      },
      [config.questionPublicKey, mutate],
    ),
  });

  const joinAsFollower = React.useCallback(async () => {
    try {
      const response = await emit(FollowerRoutes.Join, undefined);
      setGameState(response);
    } catch (error) {
      setGameState({
        gameState: GameStateEnums.ERROR,
        gameStateData: {},
        errorCode: ClientErrorCodes.FOLLOWER_JOIN_QUIZ_ERROR,
      });
    }
  }, [emit]);

  return {
    joinAsFollower,
    gameState,
    connectedPlayers,
    respondents,
    connectedWithDelay,
    timeOffset,
  };
};
